import i18n from '@/utils/i18n';
import { AdditionalInputValueInput } from '@/types';
import { captureException } from '@/utils/sentry';
import {
  CartInputsHookParams,
  CartInputsHookValue,
} from '@/contexts/new-order-actions-context-types';
import { getCartKeyForOrder, InputValueMap } from '@/utils/new-order';
import { OrderSummaryInputID } from '@/enums';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';

const EMPTY_INPUT_VALUE_MAP: InputValueMap = {};
const PERSIST_DEBOUNCE_DELAY_MS = 1000;

/**
 * The PO number, order notes and supplier order fields of the active cart.
 * Kept per cart so switching does not leak one cart's values into another, and
 * written to the order row as they are typed so they survive a switch or another device.
 */
export const useCartInputs = ({
  updateOrderAdditionalInputs,
  updateOrderNotes,
  updateOrderPoNumber,
}: CartInputsHookParams): CartInputsHookValue => {
  const { draftOrder } = useNewOrderContext();
  const isOfflineModeActive = useOfflineModeActive();

  const [inputValuesByCart, setInputValuesByCart] = useState<{
    [cartKey: string]: InputValueMap;
  }>({});
  const cartKey = getCartKeyForOrder(draftOrder);
  const additionalInputValueMap =
    inputValuesByCart[cartKey] ?? EMPTY_INPUT_VALUE_MAP;

  const inputValuesRef = useRef(inputValuesByCart);
  inputValuesRef.current = inputValuesByCart;

  const updateActiveCartInputs = useCallback(
    (update: (previous: InputValueMap) => InputValueMap) => {
      setInputValuesByCart((previous) => {
        const current = previous[cartKey] ?? EMPTY_INPUT_VALUE_MAP;
        const next = update(current);
        return next === current ? previous : { ...previous, [cartKey]: next };
      });
    },
    [cartKey],
  );

  const editedInputIDsByCartRef = useRef<{ [orderUUID: string]: Set<string> }>(
    {},
  );

  const persistInputs = useCallback(
    async (orderUUID: string, inputValueMap: InputValueMap) => {
      if (isOfflineModeActive) {
        return;
      }
      const editedInputIDs = editedInputIDsByCartRef.current[orderUUID];
      if (!editedInputIDs?.size) {
        return;
      }

      const additionalInputValues = [];
      let hasEditedAdditionalInput = false;
      const writes: Promise<unknown>[] = [];

      for (const [inputID, { title, value }] of Object.entries(inputValueMap)) {
        switch (inputID) {
          case OrderSummaryInputID.Notes:
            if (editedInputIDs.has(inputID)) {
              writes.push(
                updateOrderNotes({
                  variables: { orderNotes: value, orderUUID },
                }),
              );
            }
            break;
          case OrderSummaryInputID.PONumber:
            if (editedInputIDs.has(inputID)) {
              writes.push(
                updateOrderPoNumber({
                  variables: { orderUUID, poNumber: value },
                }),
              );
            }
            break;
          default:
            additionalInputValues.push({ id: inputID, title, value });
            if (editedInputIDs.has(inputID)) {
              hasEditedAdditionalInput = true;
            }
            break;
        }
      }
      if (hasEditedAdditionalInput) {
        writes.push(
          updateOrderAdditionalInputs({
            variables: { additionalInputValues, orderUUID },
          }),
        );
      }

      const writtenInputIDs = [...editedInputIDs];
      for (const inputID of writtenInputIDs) {
        editedInputIDs.delete(inputID);
      }

      try {
        await Promise.all(writes);
      } catch (error) {
        for (const inputID of writtenInputIDs) {
          editedInputIDs.add(inputID);
        }
        captureException(error);
      }
    },
    [
      isOfflineModeActive,
      updateOrderAdditionalInputs,
      updateOrderNotes,
      updateOrderPoNumber,
    ],
  );

  const debouncedPersistInputs = useDebouncedCallback(
    persistInputs,
    PERSIST_DEBOUNCE_DELAY_MS,
  );

  // Switching customers remounts the tree, so flush the pending write instead of losing it.
  useEffect(
    () => () => debouncedPersistInputs.flush(),
    [debouncedPersistInputs],
  );

  const draftOrderUUID = draftOrder?.uuid;
  const savedAdditionalInputValues = draftOrder?.additional_input_values as
    | AdditionalInputValueInput[]
    | { [id: string]: { title: string; value: string } }
    | null
    | undefined;
  const savedNotes = draftOrder?.notes;
  const savedPoNumber = draftOrder?.po_number;

  useEffect(() => {
    if (!draftOrderUUID) {
      return;
    }
    updateActiveCartInputs((previous) => {
      const missing: InputValueMap = {};

      const savedInputs = Array.isArray(savedAdditionalInputValues)
        ? savedAdditionalInputValues
        : Object.entries(savedAdditionalInputValues ?? {}).map(
            ([id, input]) => ({ id, ...input }),
          );
      for (const { id, title, value } of savedInputs) {
        if (!previous[id]) {
          missing[id] = { title, value };
        }
      }

      if (!previous[OrderSummaryInputID.Notes] && savedNotes) {
        missing[OrderSummaryInputID.Notes] = {
          title: i18n.t('order_notes'),
          value: savedNotes,
        };
      }

      if (!previous[OrderSummaryInputID.PONumber] && savedPoNumber) {
        missing[OrderSummaryInputID.PONumber] = {
          title: i18n.t('po_number'),
          value: savedPoNumber,
        };
      }

      return Object.keys(missing).length > 0
        ? { ...previous, ...missing }
        : previous;
    });
  }, [
    draftOrderUUID,
    savedAdditionalInputValues,
    savedNotes,
    savedPoNumber,
    updateActiveCartInputs,
  ]);

  const setAdditionalInputValue = useCallback(
    (inputID: string, inputTitle: string, inputValue: string) => {
      const current = inputValuesRef.current[cartKey] ?? EMPTY_INPUT_VALUE_MAP;
      const nextMap = {
        ...current,
        [inputID]: { title: inputTitle, value: inputValue },
      };
      inputValuesRef.current = {
        ...inputValuesRef.current,
        [cartKey]: nextMap,
      };
      setInputValuesByCart(inputValuesRef.current);

      if (draftOrder?.uuid) {
        editedInputIDsByCartRef.current[draftOrder.uuid] ??= new Set();
        editedInputIDsByCartRef.current[draftOrder.uuid].add(inputID);
        debouncedPersistInputs(draftOrder.uuid, nextMap);
      }
    },
    [cartKey, debouncedPersistInputs, draftOrder?.uuid],
  );

  const clearActiveCartInputs = useCallback(
    () => updateActiveCartInputs(() => ({})),
    [updateActiveCartInputs],
  );

  return {
    additionalInputValueMap,
    cancelPendingInputs: debouncedPersistInputs.cancel,
    clearActiveCartInputs,
    flushPendingInputs: debouncedPersistInputs.flush,
    setAdditionalInputValue,
  };
};
